export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const DAY_ABBREV = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const createEmptyDay = () => ({
  enabled: false,
  periods: [],
});

export const createEmptyWeeklySchedule = () => ({
  days: DAY_NAMES.map(() => createEmptyDay()),
});

export const createAlwaysSchedule = () => ({
  days: DAY_NAMES.map(() => ({
    enabled: true,
    periods: [{ startMinutes: 0, endMinutes: 1440 }],
  })),
});

export const createNeverSchedule = () => ({
  days: DAY_NAMES.map(() => ({
    enabled: false,
    periods: [],
  })),
});

export const createWeekdaysSchedule = () => ({
  days: DAY_NAMES.map((_, index) => ({
    enabled: index >= 1 && index <= 5,
    periods: index >= 1 && index <= 5 ? [{ startMinutes: 360, endMinutes: 1080 }] : [],
  })),
});

export const createBusinessHoursSchedule = () => ({
  days: DAY_NAMES.map((_, index) => ({
    enabled: index >= 1 && index <= 5,
    periods: index >= 1 && index <= 5 ? [{ startMinutes: 540, endMinutes: 1020 }] : [],
  })),
});

/** Stable ids for the always-available preset timetables, seeded on page load. */
export const PRESET_TIMETABLE_IDS = {
  ALWAYS: 'preset-always',
  BUSINESS: 'preset-business',
  WEEKDAYS: 'preset-weekdays',
  NEVER: 'preset-never',
};

/** Common timetables available immediately, with no setup required. */
export const createDefaultTimetables = () => [
  {
    id: PRESET_TIMETABLE_IDS.ALWAYS,
    name: 'Always (24/7)',
    weeklySchedule: createAlwaysSchedule(),
    scheduleType: 'always',
    isPreset: true,
  },
  {
    id: PRESET_TIMETABLE_IDS.BUSINESS,
    name: 'Business Hours',
    weeklySchedule: createBusinessHoursSchedule(),
    scheduleType: 'weekly',
    isPreset: true,
  },
  {
    id: PRESET_TIMETABLE_IDS.WEEKDAYS,
    name: 'Weekdays',
    weeklySchedule: createWeekdaysSchedule(),
    scheduleType: 'weekly',
    isPreset: true,
  },
  {
    id: PRESET_TIMETABLE_IDS.NEVER,
    name: 'Never',
    weeklySchedule: createNeverSchedule(),
    scheduleType: 'never',
    isPreset: true,
  },
];

/** Color + icon used consistently for a schedule type across cards, chips and selects. */
export const getScheduleTypeColor = (type) => {
  switch (type) {
    case 'always':
      return 'success';
    case 'never':
      return 'error';
    case 'weekly':
      return 'info';
    default:
      return 'default';
  }
};

export const getScheduleTypeIcon = (type) => {
  switch (type) {
    case 'always':
      return 'mdi:clock-check';
    case 'never':
      return 'mdi:clock-remove';
    case 'weekly':
      return 'mdi:calendar-clock';
    default:
      return 'mdi:calendar-blank';
  }
};

/** Gate-In/Out device ids can come back as either camelCase or PascalCase, or singular fields. */
export const getGateInDeviceIds = (gate) => {
  if (gate.inDeviceIds?.length) return gate.inDeviceIds.map(Number);
  if (gate.InDeviceIds?.length) return gate.InDeviceIds.map(Number);
  if (gate.inDeviceId != null && gate.inDeviceId !== '') return [Number(gate.inDeviceId)];
  if (gate.InDeviceId != null && gate.InDeviceId !== '') return [Number(gate.InDeviceId)];
  return [];
};

export const getGateOutDeviceIds = (gate) => {
  if (gate.outDeviceIds?.length) return gate.outDeviceIds.map(Number);
  if (gate.OutDeviceIds?.length) return gate.OutDeviceIds.map(Number);
  if (gate.outDeviceId != null && gate.outDeviceId !== '') return [Number(gate.outDeviceId)];
  if (gate.OutDeviceId != null && gate.OutDeviceId !== '') return [Number(gate.OutDeviceId)];
  return [];
};

export const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/** Derive the 'always' | 'never' | 'weekly' type from the raw weekly schedule content. */
export const computeScheduleType = (weeklySchedule) => {
  const allDaysEnabled = weeklySchedule.days.every((d) => d.enabled);
  const noDaysEnabled = weeklySchedule.days.every((d) => !d.enabled);
  const allDays247 = weeklySchedule.days.every(
    (d) => d.enabled && d.periods.length === 1 && d.periods[0].startMinutes === 0 && d.periods[0].endMinutes === 1440
  );

  if (noDaysEnabled) return 'never';
  if (allDaysEnabled && allDays247) return 'always';
  return 'weekly';
};

/** Short human-readable summary of a weekly schedule, for lists/chips. */
export const summarizeWeeklySchedule = (weeklySchedule) => {
  if (!weeklySchedule?.days) return 'Not configured';

  const type = computeScheduleType(weeklySchedule);
  if (type === 'always') return 'Always (24/7)';
  if (type === 'never') return 'Never';

  const activeDays = weeklySchedule.days
    .map((day, index) => ({ ...day, index }))
    .filter((day) => day.enabled && day.periods.length > 0);

  if (activeDays.length === 0) return 'Never';

  // If every active day shares the exact same single period, show it compactly.
  const first = activeDays[0];
  const samePeriodEveryDay =
    first.periods.length === 1 &&
    activeDays.every(
      (d) =>
        d.periods.length === 1 &&
        d.periods[0].startMinutes === first.periods[0].startMinutes &&
        d.periods[0].endMinutes === first.periods[0].endMinutes
    );

  const dayLabels = activeDays.map((d) => DAY_ABBREV[d.index]).join(', ');

  if (samePeriodEveryDay) {
    return `${dayLabels} · ${minutesToTime(first.periods[0].startMinutes)}-${minutesToTime(first.periods[0].endMinutes)}`;
  }

  return `${activeDays.length} day(s) active`;
};
